import i18n from '@/utils/i18n';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Avatar, createAvatarProps } from '@usepepper/ui/atoms/avatar';
import { createStyles } from '@/contexts/theme-context';
import { DropdownTrigger } from '@usepepper/ui/drafts/dropdown-trigger';
import { GestureResponderEvent, Pressable, View } from 'react-native';
import { ListItem } from '@usepepper/ui/molecules/list-item';
import {
  SalesHubAccount,
  SalesHubAccountOption,
} from '@/experiences/sales-portal/blocs/loaders/use-account-list-loader';
import {
  SearchableDropdownMenu,
  SearchableDropdownMenuRef,
} from '@usepepper/ui/molecules/searchable-dropdown-menu';
import { Typography } from '@usepepper/ui/atoms/typography';

const ANCHOR_WIDTH = 240;
const DROPDOWN_WIDTH = 300;
const MENU_ITEM_HEIGHT = 48;

type AccountOptionData = SalesHubAccountOption & { key: string };

const toOptionData = (accounts: SalesHubAccountOption[]): AccountOptionData[] =>
  accounts.map((a) => ({ ...a, key: a.chatId }));

interface AccountOptionProps {
  address?: string;
  avatarSeed: string;
  isFocused?: boolean;
  isSelected: boolean;
  name: string;
  onSelect: () => void;
}

const AccountOption: React.FC<AccountOptionProps> = ({
  address,
  avatarSeed,
  isFocused = false,
  isSelected,
  name,
  onSelect,
}) => {
  const [styles, theme] = useStyles();
  const avatarProps = createAvatarProps(name, avatarSeed);

  const handlePress = (e: GestureResponderEvent) => {
    // Prevent the dropdown overlay from treating this press as an
    // outside-click dismiss before the selection handler runs.
    e.stopPropagation();
    onSelect();
  };

  return (
    <Pressable
      onPress={handlePress}
      style={({ hovered }) => [
        styles.optionContainer,
        {
          backgroundColor:
            hovered || isSelected || isFocused
              ? theme.sk.color.bg.primary.hover
              : theme.sk.color.bg.primary.default,
        },
      ]}
    >
      <ListItem
        leftElement={<Avatar {...avatarProps} size="small" />}
        style={styles.optionListItem}
        subtitle={
          address ? (
            <Typography
              color={theme.sk.color.text.secondary}
              numberOfLines={1}
              variant="paragraph-sm"
            >
              {address}
            </Typography>
          ) : undefined
        }
        title={
          <Typography
            color={theme.sk.color.text.primary}
            numberOfLines={1}
            variant="paragraph-md-600"
          >
            {name}
          </Typography>
        }
      />
    </Pressable>
  );
};

interface AccountSelectorProps {
  account: SalesHubAccount | null;
  accounts: SalesHubAccountOption[];
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onSelectAccount: (account: SalesHubAccountOption) => void;
}

/**
 * Shared Sales Hub account (customer) selector. Wraps
 * `SearchableDropdownMenu` with avatar-prefixed options and client-side
 * name/address filtering.
 */

export const AccountSelector: React.FC<AccountSelectorProps> = ({
  account,
  accounts,
  isOpen,
  onOpenChange,
  onSelectAccount,
}) => {
  const [styles, theme] = useStyles();
  const [searchText, setSearchText] = useState('');
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const dropdownRef = useRef<SearchableDropdownMenuRef>(null);

  // Focus the search input when the dropdown opens
  useEffect(() => {
    if (isOpen) {
      dropdownRef.current?.focusSearchInput();
    }
  }, [isOpen]);

  const filteredAccounts = useMemo(() => {
    const options = toOptionData(accounts);
    if (!searchText) {
      return options;
    }
    const lower = searchText.toLowerCase();
    return options.filter(
      (a) =>
        a.name.toLowerCase().includes(lower) ||
        a.address.toLowerCase().includes(lower),
    );
  }, [accounts, searchText]);

  const handleClose = () => {
    setSearchText('');
    setFocusedIndex(null);
    onOpenChange(false);
  };

  const handleSelectAccount = (item: AccountOptionData) => {
    handleClose();
    if (item.chatId !== account?.chatId) {
      onSelectAccount({
        address: item.address,
        chatId: item.chatId,
        currencyCode: item.currencyCode,
        name: item.name,
        restaurantUUID: item.restaurantUUID,
        supplierUUID: item.supplierUUID,
      });
    }
  };

  const triggerAvatarProps = createAvatarProps(
    account?.name ?? '',
    account?.chatId,
  );

  const trigger = (
    <DropdownTrigger
      accessibilityLabel={
        account?.name ?? i18n.t('sales_portal.account_selector.placeholder')
      }
      isOpen={isOpen}
      onPress={() => onOpenChange(true)}
      style={styles.trigger}
    >
      <View style={styles.selectedAccount}>
        {account ? <Avatar {...triggerAvatarProps} size="small" /> : null}
        <Typography
          color={
            account ? theme.sk.color.text.primary : theme.sk.color.text.tertiary
          }
          flexShrink={1}
          numberOfLines={1}
          variant="paragraph-md"
        >
          {account?.name ?? i18n.t('sales_portal.account_selector.placeholder')}
        </Typography>
      </View>
    </DropdownTrigger>
  );

  const emptyList = (
    <View style={styles.emptyContainer}>
      <Typography color={theme.sk.color.text.secondary} variant="paragraph-md">
        {i18n.t('sales_portal.account_selector.no_results_found')}
      </Typography>
    </View>
  );

  return (
    <SearchableDropdownMenu
      dropdownContainerOverrides={styles.dropdownContainer}
      estimatedListItemHeight={MENU_ITEM_HEIGHT}
      focusedIndex={focusedIndex}
      isVisible={isOpen}
      listData={filteredAccounts}
      listEmptyComponent={emptyList}
      onClose={handleClose}
      onFocusedIndexChange={setFocusedIndex}
      onSearch={setSearchText}
      placeholderText={i18n.t(
        'sales_portal.account_selector.search_placeholder',
      )}
      position="bottom-start"
      ref={dropdownRef}
      renderItem={(item, _index, isFocused) => (
        <AccountOption
          address={item.address}
          avatarSeed={item.chatId}
          isFocused={isFocused}
          isSelected={account?.chatId === item.chatId}
          key={item.key}
          name={item.name}
          onSelect={() => handleSelectAccount(item)}
        />
      )}
      searchText={searchText}
      trigger={trigger}
    />
  );
};

const useStyles = createStyles((theme) => ({
  dropdownContainer: {
    paddingHorizontal: theme.sk.spacing.spaceXsmall,
    paddingVertical: theme.sk.spacing.spaceSmall,
    width: DROPDOWN_WIDTH,
  },
  emptyContainer: {
    alignItems: 'center',
    height: MENU_ITEM_HEIGHT,
    justifyContent: 'center',
  },
  optionContainer: {
    borderRadius: theme.sk.radii.radiusSmall,
    padding: theme.sk.spacing.spaceSmall,
  },
  optionListItem: {
    alignItems: 'center',
  },
  selectedAccount: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
  },
  trigger: {
    gap: theme.sk.spacing.spaceSMedium,
    height: theme.sk.spacing.spaceXlarge,
    minHeight: theme.sk.spacing.spaceXlarge,
    width: ANCHOR_WIDTH,
  },
}));
